import { NetworkStatus } from '@apollo/client'
import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import { GetFilterInput } from '@deltek/specification-client/core/Models/projectItem'
import { updateKpMainLocalStore } from '@deltek/specification-client/core/Utilities/localstorageUtils'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    SpecFilters,
    SpecTable,
    TableFetchDataProps,
    TableFetchNextDataProps,
} from '@deltek/specification-client/core/Widgets/SpecTable'
import FilterSelectText from '@deltek/specification-client/core/Widgets/SpecTable/filters/FilterSelectText'
import useSpecTableOptions from '@deltek/specification-client/core/Widgets/SpecTable/useSpecTableOptions'
import { Theme, alpha, makeStyles } from '@material-ui/core/styles'
import { GetProjectElementsResponse } from 'api/graphQL/types/projectElement.types'
import clsx from 'clsx'
import format from 'date-fns/format'
import { LastWorkedOnDetails } from 'models/lastWorkedOnDetails'
import { ProjectElement } from 'models/projectElements'
import React from 'react'
import { useParams } from 'react-router-dom'
import { Column, Filters } from 'react-table'
import { LODS, STATUS, TYPEAE } from 'shared/utils/dropdownUtils'
import { getLocalStorageValue } from '../../projectUtils'
import {
    mapFilterKvpToFiltersModel,
    remapFilterDataType,
} from '../projectOverviewUtils'
import LastWorkedOnItemBlock from './LastWorkedOnItemBlock'
import WorkspaceProgressNoData from './WorkspaceProgressNoData'
import WorkspaceProgressStatusChip, {
    WorkspaceProgressStatus,
} from './WorkspaceProgressStatusChip'

const TableHeight = 647
const localStorageItemKey = (userId: string) =>
    `workspaceProgressTable${userId}`

const useStyles = makeStyles((theme: Theme) => ({
    tableContainer: {
        padding: theme.spacing(0, 1.5),
        '& .table-title-settings-container': {
            padding: 0,
            backgroundColor: theme.palette.common.white,
            '& .table-title-container': {
                ...theme.typography.h1,
                color: theme.palette.primary.main,
                textTransform: 'uppercase',
            },
            '& .clear-filter-container': {
                display: 'flex',
                alignItems: 'center',
            },
            '& button.clear-filter-button': {
                color: theme.palette.primary.light,
            },
            '& button.filter-option-button': {
                color: theme.palette.primary.main,
            },
        },
        '& .sticky.table': {
            overflowX: 'hidden',
            overflowY: 'auto',
            border: '1px solid rgba(166, 137, 102, 0.25)',
            borderRadius: theme.spacing(0.5),
        },
        '& .header': {
            fontWeight: 500,
            '& > .tr': {
                border: 'none',
                '& .th:first-child > div, .th:first-child > div > div > div ': {
                    backgroundColor: theme.palette.secondary.light,
                    color: theme.palette.common.white,
                    fontSize: pxToRem(16),
                    textAlign: 'start',
                    '& .MuiInput-underline:after': {
                        borderBottomColor: theme.palette.common.white,
                    },
                    '& .MuiInput-underline:before': {
                        borderBottomColor: alpha(
                            theme.palette.common.white,
                            0.5
                        ),
                    },
                },
                '& .th': {
                    background: '#FFFDFA',
                },
                '& .th:not(:first-child) > div > div > div': {
                    fontSize: pxToRem(13),
                    background: '#FFFDFA',
                    color: '#402E32',

                    '&:first-child': {
                        maxHeight: 40,
                    },
                },
                '& input': {
                    paddingTop: theme.spacing(1.1125),
                    paddingBottom: theme.spacing(0.5),
                    fontSize: pxToRem(12),
                },
                '& .header-filter-container': {
                    paddingBottom: theme.spacing(1.25),
                },
                '& .filter-select-state': {
                    fontSize: pxToRem(12),
                    paddingBottom: theme.spacing(0.25),
                },
            },
        },
        '& .body': {
            '& > .tr': {
                borderBottom: 'none',
                '& > .td': {
                    borderBottom: '1px solid rgba(166, 137, 102, 0.25)',
                    fontSize: pxToRem(12),
                    maxHeight: pxToRem(600),
                    alignItems: 'center',
                    justifyContent: 'center',
                },
                '& > .td:nth-child(2)': {
                    // This is to override the in-line styles
                    // generated by SpecTable.
                    textOverflow: 'unset !important',
                },
                '& > .td:not(:first-child)': {
                    margin: 0,
                    paddingTop: theme.spacing(3.5),
                    background: '#FFFDFA',
                    color: theme.palette.common.black,
                },
                '& > .td:first-child': {
                    backgroundColor: theme.palette.secondary.light,
                    paddingRight: `${theme.spacing(1.5)}px !important`,
                },

                '&:last-child > .td': {
                    padding: 0,
                    borderBottom: 'none',
                    // This is to remove the inline min-height from SpecTable
                    minHeight: '0 !important',
                },
            },
            '& .MuiSkeleton-text': {
                transform: 'unset',
            },
            '& .td': {
                fontSize: pxToRem(12),
            },
        },
        '& .tr': {
            textAlign: 'center',
        },
    },
    tableContainerData: {
        '& .body': {
            '& > .tr': {
                borderBottom: 'none',
                '&:first-child': {
                    borderTopColor: 'rgba(166, 137, 102, 0.25)',
                },
            },
        },
    },
    tableContainerNoData: {
        '& .body': {
            '& > .tr': {
                borderBottom: 'none',
                '&:first-child': {
                    borderTop: 'none',
                },
            },
        },
    },
    textContainerWrap: {
        whiteSpace: 'normal',
    },
    tooltip: {
        border: 0,
    },
}))

export interface WorkspaceTableItem {
    lastWorkedOn: LastWorkedOnDetails
    state: string
    assignedUser: string
    lastUpdated: string
    targetLod: number
    currentLod: number
    dataType: string
}

const ProjectElementDataTypeMapping: Record<string, string> = {
    ProjectNugget: 'Product Type',
    ProjectUniformatClassification: 'Assembly',
    ProjectProductFamily: 'Family',
}

export interface WorkspaceProgressTableProps {
    userId?: string
    localStorageKey?: string
    workspaceProgressData?: GetProjectElementsResponse
    workspaceProgressLoading: boolean
    getProjectElements: (options?: any) => void
    handleFavoriteChange?: (favorite: boolean, projectId: string) => void
    fetchNextData?: (
        props: Partial<TableFetchNextDataProps<WorkspaceTableItem>>
    ) => void
    projectElementsCount: number
    projectElementsStatus: number
    setSkipCount: (val: number) => void
    setProgressTableFilters: React.Dispatch<
        React.SetStateAction<GetFilterInput[] | undefined>
    >
}

const WorkspaceProgressTable: React.FC<WorkspaceProgressTableProps> = ({
    userId = '',
    localStorageKey = '',
    workspaceProgressData,
    workspaceProgressLoading,
    getProjectElements,
    fetchNextData,
    projectElementsCount,
    projectElementsStatus,
    setSkipCount,
    setProgressTableFilters,
}) => {
    const classes = useStyles()
    const { projectId } = useParams<{ projectId?: string }>()
    const divRef = React.useRef<HTMLDivElement>()
    const savedTableFilterBarVisible = React.useMemo(() => {
        if (userId) {
            const saved = getLocalStorageValue(
                localStorageItemKey(userId),
                userId,
                '',
                localStorageKey
            )
            const pastItems = saved !== '' ? JSON.parse(saved as string) : {}
            if (pastItems.tableFilterBarVisible) {
                return pastItems.tableFilterBarVisible
            }

            return undefined
        }
    }, [userId, localStorageKey])
    const [tableFilterBarVisible, setTableFilterBarVisible] = React.useState(
        false
    )
    const [
        isClearAllFilterButtonVisible,
        setIsClearAllFilterButtonVisible,
    ] = React.useState(false)

    const [currentFilters, setCurrentFilters] = React.useState<
        SpecFilters<WorkspaceTableItem>
    >()

    const formattedWorkspaceProgressData: WorkspaceTableItem[] = React.useMemo(() => {
        if (
            workspaceProgressData &&
            (workspaceProgressData?.getProjectElements?.count ?? 0) > 0
        ) {
            return workspaceProgressData.getProjectElements.projectElements
                .map((projectElement: ProjectElement) => ({
                    lastWorkedOn: {
                        elementId: projectElement.id,
                        name: projectElement.name,
                        level:
                            ProjectElementDataTypeMapping[
                                projectElement.dataType
                            ],
                    },
                    state: projectElement.projectElementMetadata.state,
                    assignedUser:
                        projectElement.projectElementMetadata.assignedUser,
                    lastUpdated: format(
                        new Date(projectElement.metadata.updated ?? ''),
                        'MM/dd/yyyy'
                    ),
                    currentLod:
                        projectElement.projectElementMetadata.currentLOD ?? 100,
                    targetLod:
                        projectElement.projectElementMetadata.targetLOD ?? 100,
                    dataType:
                        ProjectElementDataTypeMapping[projectElement.dataType],
                }))
                .concat({
                    lastWorkedOn: {
                        name: '',
                        level: '',
                        elementId: '',
                    },
                    state: '',
                    assignedUser: '',
                    lastUpdated: '',
                    currentLod: 0,
                    targetLod: 0,
                    dataType: '',
                }) as WorkspaceTableItem[]
        }
        return []
    }, [workspaceProgressData])

    const handleTableFilterBarDisplayToggle = () => {
        updatePersistentConfigs('tableFilterBarVisible', !tableFilterBarVisible)
        setTableFilterBarVisible(!tableFilterBarVisible)
    }

    const updateFilters = React.useCallback(
        (props: TableFetchDataProps<WorkspaceTableItem>) => {
            const newFilters =
                (props.filters?.length ?? 0) > 0 ? props.filters : []
            if (
                (!currentFilters && newFilters) ||
                (currentFilters && !isEqual(currentFilters, newFilters))
            ) {
                setCurrentFilters(newFilters ?? [])
                setIsClearAllFilterButtonVisible(
                    newFilters && newFilters?.length > 0 ? true : false
                )
                setSkipCount(0)
            }
        },
        [setCurrentFilters, currentFilters, setSkipCount]
    )

    const updatePersistentConfigs = React.useCallback(
        (
            propKey: string,
            inputValue: Filters<WorkspaceTableItem> | boolean | any
        ) => {
            if (userId) {
                const saved = getLocalStorageValue(
                    localStorageItemKey(userId),
                    userId,
                    '',
                    localStorageKey
                )
                const pastItems =
                    saved !== '' ? JSON.parse(saved as string) : {}
                updateKpMainLocalStore({
                    currentUserId: userId,
                    pageKey: localStorageKey,
                    itemKey: localStorageItemKey(userId),
                    itemValue: JSON.stringify({
                        ...pastItems,
                        [propKey]: inputValue,
                    }),
                })
            }
        },
        [localStorageKey, userId]
    )

    React.useEffect(() => {
        const mappedFilters = mapFilterKvpToFiltersModel(currentFilters)

        const remappedFilter = remapFilterDataType([...mappedFilters])

        setProgressTableFilters(remappedFilter)

        if (currentFilters !== undefined) {
            updatePersistentConfigs('filters', currentFilters)
        }
        getProjectElements({
            variables: {
                input: {
                    projectId: projectId ?? '',
                    limit: 100,
                    sortOrders: [
                        {
                            sortField: 'lastUpdated',
                            sortOrder: 'desc',
                        },
                    ],
                    filters: remappedFilter,
                },
            },
        })
    }, [
        currentFilters,
        getProjectElements,
        projectId,
        updatePersistentConfigs,
        setProgressTableFilters,
    ])

    const savedFilters = React.useMemo(() => {
        if (userId) {
            const saved = getLocalStorageValue(
                localStorageItemKey(userId),
                userId,
                '',
                localStorageKey
            )
            const pastItems = saved !== '' ? JSON.parse(saved as string) : {}
            if (pastItems.filters) {
                return pastItems.filters
            }

            return undefined
        }
    }, [userId, localStorageKey])

    React.useEffect(() => {
        setCurrentFilters(savedFilters)
    }, [savedFilters])

    React.useEffect(() => {
        if (savedTableFilterBarVisible !== undefined) {
            setTableFilterBarVisible(savedTableFilterBarVisible)
        }
    }, [savedTableFilterBarVisible])

    const tableProps = React.useMemo(() => {
        return {
            skeletonItemHeight: tableFilterBarVisible ? 550 : 598,
            allowGrouping: false,
            divContainerRef: divRef,
            fetchDataLoading:
                projectElementsStatus !== NetworkStatus.ready &&
                projectElementsStatus !== NetworkStatus.fetchMore,
            fetchData: updateFilters,
            fetchNextData: fetchNextData,
            fetchNextDataLoading: projectElementsStatus !== NetworkStatus.ready,
            pageSize: 100,
            filters: savedFilters,
            height: TableHeight,
            totalItems: projectElementsCount,
            showCheckboxColumn: false,
            defaultColumn: {
                minWidth: 20,
                width: 130,
                disableSortBy: true,
            } as Column<WorkspaceTableItem>,
            specTableTitleSettings: {
                tableSettings: {
                    initialPinningProps: {
                        firstColumnPinned: false,
                        lastColumnPinned: false,
                    },
                },
                showFiltersRow: tableFilterBarVisible,
                showTableSettings: false,
                onFilterClick: handleTableFilterBarDisplayToggle,
                showClearAllFilterButton: isClearAllFilterButtonVisible,
                tableTitle: 'Design Progress',
            },
            data: formattedWorkspaceProgressData,
            columns: [
                {
                    Header: 'Element Name',
                    accessor: 'lastWorkedOn',
                    style: { backgroundColor: '#B7A99A' },
                    Cell: ({ value }: { value: LastWorkedOnDetails }) =>
                        value.level || value.name ? (
                            <LastWorkedOnItemBlock item={value} />
                        ) : (
                            <div
                                style={{
                                    height:
                                        TableHeight -
                                        (formattedWorkspaceProgressData.length -
                                            1) *
                                            70 -
                                        (tableFilterBarVisible ? 90 : 43),
                                }}
                            />
                        ),
                    minWidth:
                        formattedWorkspaceProgressData.length > 7 ? 236 : 246,
                    width:
                        formattedWorkspaceProgressData.length > 7 ? 236 : 246,
                },
                {
                    Header: 'State',
                    accessor: 'state',
                    minWidth: 130,
                    width: 130,
                    Cell: ({ value }: { value: string }) =>
                        value && (
                            <WorkspaceProgressStatusChip
                                variant={
                                    value.toLowerCase() as WorkspaceProgressStatus
                                }
                            />
                        ),
                    Filter: FilterSelectText({
                        options: STATUS.map((item) => item.name),
                    }),
                },
                {
                    Header: 'Type',
                    accessor: 'dataType',
                    Cell: ({ value }: { value: string }) => (
                        <div className={classes.textContainerWrap}>{value}</div>
                    ),
                    Filter: FilterSelectText({
                        options: TYPEAE.map((item) => item.name),
                    }),
                    minWidth: 100,
                    width: 100,
                },
                {
                    Header: 'Assigned To',
                    accessor: 'assignedUser',
                    Cell: ({ value }: { value: string }) => (
                        <TooltipEllipsis
                            lineLimit={2}
                            className={classes.tooltip}
                            tooltipPlacements={['top']}
                            text={value}
                        >
                            <div className={classes.textContainerWrap}>
                                {value}
                            </div>
                        </TooltipEllipsis>
                    ),
                    minWidth: 120,
                    width: 120,
                },
                {
                    Header: 'Date Modified',
                    accessor: 'lastUpdated',
                    minWidth: 130,
                    width: 130,
                },
                {
                    Header: 'Current LOS',
                    accessor: 'currentLod',
                    Filter: FilterSelectText({ options: LODS }),
                    minWidth: 105,
                    width: 105,
                },
                {
                    Header: 'Target LOS',
                    accessor: 'targetLod',
                    Filter: FilterSelectText({ options: LODS }),
                    minWidth: 100,
                    width: 100,
                },
            ] as Column<WorkspaceTableItem>[],
            noDataComponent: <WorkspaceProgressNoData />,
        }
    }, [
        TableHeight,
        fetchNextData,
        updateFilters,
        savedFilters,
        isClearAllFilterButtonVisible,
        handleTableFilterBarDisplayToggle,
        tableFilterBarVisible,
        formattedWorkspaceProgressData,
        workspaceProgressData,
        workspaceProgressLoading,
        projectElementsStatus,
        projectElementsCount,
    ])
    const specTableProps = useSpecTableOptions<WorkspaceTableItem>(tableProps)

    return (
        <div
            className={clsx(
                classes.tableContainer,
                specTableProps.tableOptions.data.length > 1
                    ? classes.tableContainerData
                    : classes.tableContainerNoData
            )}
        >
            <SpecTable
                {...specTableProps}
                lazyLoading={true}
                //  tableBodyHeight={'620px'}
            />
        </div>
    )
}

export default WorkspaceProgressTable
